import React, { useCallback, useState, useEffect } from 'react';
import * as ReactDOMClient from 'react-dom/client';
import { WidgetsProvider, WidgetDataType, usePreviewSearch, widget } from '@sitecore-search/react';
import { PreviewSearch } from '@sitecore-search/ui';
import PropTypes from 'prop-types';
import GoogleIcon from '../../google-icon';
import SuggestionBlock from '../../sitecore-search-components/c-search-suggestion-block-sitecore';
import SearchFormSitecore from '../../sitecore-search-components/c-search-form-sitecore';

function MobileSearchBarSitecore({ defaultItemsPerPage = 4, searchUrl = '/search-results', dataSourceId }) {
    const [searchQuery, setSearchQuery] = useState('');
    const [showSuggestions, setShowSuggestions] = useState(false);
    /*
        Due to the current limitations of the website,
        it is best to simply observe the parent mobile menu
        component for open / closed updates
    */
    useEffect(() => {
        // Get the parent container
        const parentContainer = document.querySelector('#mobile-nav');
        if (!parentContainer) return;
        // Create observer to watch for class changes
        const observer = new MutationObserver(mutations => {
            mutations.forEach(mutation => {
                if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
                    const isOpen = parentContainer.classList.contains('is-active');
                    if (!isOpen) {
                        setShowSuggestions(false);
                    }
                }
            });
        });

        // Start observing
        observer.observe(parentContainer, {
            attributes: true,
            attributeFilter: ['class']
        });
        // Cleanup
        /* eslint-disable consistent-return */
        return () => {
            observer.disconnect();
        };
        /* eslint-disable consistent-return */
    }, []);

    const {
        actions: { onKeyphraseChange },
        queryResult: {
            isFetching,
            isLoading,
            data: {
                suggestion: {
                    title_context_aware: articleSuggestions = [],
                    user_search_history: userSearchHistory = [],
                    popular_search_page: popularSearches = [],
                    did_you_mean_title: autoCorrectSuggestions = []
                } = {}
            } = {}
        }
    } = usePreviewSearch({
        query: query => {
            query.getRequest().setSources([dataSourceId]);
        },
        state: {
            suggestionsList: [
                { suggestion: 'title_context_aware', max: defaultItemsPerPage },
                { suggestion: 'user_search_history', max: defaultItemsPerPage },
                { suggestion: 'popular_search_page', max: defaultItemsPerPage },
                { suggestion: 'did_you_mean_title', max: 1 }
            ],
            itemsPerPage: defaultItemsPerPage
        }
    });

    const loading = isLoading || isFetching;
    const keyphraseHandler = useCallback(
        event => {
            const { target } = event;
            setSearchQuery(target.value);
            onKeyphraseChange({ keyphrase: target.value });
        },
        [onKeyphraseChange]
    );

    const handleSubmit = () => {
        const targetReplace = `${searchUrl}?q=${searchQuery}`;
        window.location.href = targetReplace;
    };

    return (
        <>

            <div className="sitecore-search-bar-mobile-search-buttons-container">
                <button className="sitecore-search-bar-mobile-search-back-button back btn-skin-1" type="button" onClick={() => setShowSuggestions(false)}>
                    <GoogleIcon icon="arrow_back" />
                    <span>
                        Back
                    </span>
                </button>
            </div>
            <PreviewSearch.Root>
                <div className="sitecore-search-bar-mobile-container">
                    <div className="sitecore-search-bar-mobile-search-form-container">
                        <SearchFormSitecore
                            keyphraseHandler={keyphraseHandler}
                            searchQuery={searchQuery}
                            handleSubmit={handleSubmit}
                            onClickClear={() => {
                                setSearchQuery('');
                            }}
                            onFocus={() => setShowSuggestions(true)}
                        />
                    </div>
                    <div className={`search-bar-mobile-suggestions-container ${showSuggestions ? 'active' : ''}`}>
                        {searchQuery !== '' ? (
                            <PreviewSearch.Suggestions className="search-bar-sitecore-suggestions search-bar-suggestion">
                                <SuggestionBlock blockId="search-bar-sitecore-desktop-suggestions" className="search-suggestions" items={articleSuggestions} title="Suggested Searches" loading={loading} redirectUrl={searchUrl} />
                                <PreviewSearch.SuggestionsGroup className="search-bar-sitecore-search-text-trigger">
                                    <PreviewSearch.SuggestionTrigger>
                                        {autoCorrectSuggestions.length > 0 ? (
                                            <a className="search-bar-sitecore-search-query" href={`${searchUrl}?q=${encodeURIComponent(autoCorrectSuggestions[0].text)}`}>
                                                Did you mean
                                                {' '}
                                                &quot;{autoCorrectSuggestions[0].text}&quot; ?
                                                <GoogleIcon icon="arrow_forward" />
                                            </a>
                                        ) : (
                                            <a className="search-bar-sitecore-search-query" href={`${searchUrl}?q=${encodeURIComponent(searchQuery)}`}>
                                                Search for
                                                {' '}
                                                {searchQuery}
                                                <GoogleIcon icon="arrow_forward" />
                                            </a>
                                        )}
                                    </PreviewSearch.SuggestionTrigger>
                                </PreviewSearch.SuggestionsGroup>
                            </PreviewSearch.Suggestions>
                        )
                            : (
                                <>
                                    {
                                        popularSearches.length > 0 && (
                                            <PreviewSearch.Suggestions className="search-bar-sitecore-suggestions search-bar-popular">
                                                <SuggestionBlock blockId="search-bar-sitecore-desktop-popular" className="search-suggestions" items={popularSearches} title="Popular Searches" loading={loading} redirectUrl={searchUrl} />
                                            </PreviewSearch.Suggestions>
                                        )
                                    }
                                    {
                                        userSearchHistory.length > 0 && (
                                            <PreviewSearch.Suggestions className="search-bar-sitecore-suggestions search-bar-history">
                                                <SuggestionBlock blockId="search-bar-sitecore-desktop-history" className="search-suggestions" items={userSearchHistory} title="Search History" loading={loading} prefixIcon="history" redirectUrl={searchUrl} />
                                            </PreviewSearch.Suggestions>
                                        )
                                    }
                                </>
                            )}
                    </div>
                </div>
            </PreviewSearch.Root>
        </>
    );
}

MobileSearchBarSitecore.propTypes = {
    defaultItemsPerPage: PropTypes.number.isRequired,
    searchUrl: PropTypes.string.isRequired,
    dataSourceId: PropTypes.string.isRequired
};

const PreviewSearchMobileWidget = widget(MobileSearchBarSitecore, WidgetDataType.PREVIEW_SEARCH, 'content');

const MobileSearchBarSitecoreInit = ({ el, props }) => {
    const {
        env = 'apse2',
        customerKey = '186718190-16785702162',
        apiKey = '01-3dab73b8-a842cb674e5998facb9344effba40e34541a4e04',
        dataSourceId = '1190926',
        searchUrl= '/search-results',
        previewSearchWidgetId = 'rfkid_7'
    } = props;

    const root = ReactDOMClient.createRoot(el);
    root.render(
        <WidgetsProvider
            env={env}
            customerKey={customerKey}
            apiKey={apiKey}
            publicSuffix={true}
        >
            <PreviewSearchMobileWidget defaultItemsPerPage={4} rfkId={previewSearchWidgetId} searchUrl={searchUrl} dataSourceId={dataSourceId}/>
        </WidgetsProvider>
    );
};

export default MobileSearchBarSitecoreInit;
